// src/components/wellfinder/CandidatePanel.jsx
//
// The ranked candidate list. Read-only in 3b; in 3c the selected row becomes
// the adopt target, which is why selection lives in useWfQueueStore rather than
// here.
//
// ── THE API FORM IS LABELLED, DELIBERATELY ───────────────────────────────────
// Candidates carry the RRC registry's own identifier, which is EIGHT
// characters (00347013). The record stores ten (4200347013), derived as
// '42' || api at adopt time. Two different strings for the same well, one of
// which is what the analyst will find in RRC systems and the other is what the
// deliverable will contain. Showing either unlabelled invites the analyst to
// transcribe the wrong one into a note or an email.
//
// 3c OWES A COMMIT PREVIEW showing the api10 that adoption would actually
// write. Not built here — 3b writes nothing, so there is no "would write" to
// preview yet — but the requirement is recorded so it does not get lost between
// phases.

import { Crosshair } from "lucide-react";
import LeaseName from "./LeaseName";
import { holderWording } from "./holderWording";
import { fmtDistance } from "./fmtDistance";

const bearingLabel = (b) => (b ? ` ${b}` : "");


function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[10px] font-mono uppercase tracking-wide text-fg-secondary/70">{label}</div>
      <div className="truncate text-fg-secondary">{children ?? "—"}</div>
    </div>
  );
}

// Registry records within 2m of each other. Measured across the corpus: 85 of
// 3,924 multi-candidate detections have their top two within 2m, 66 within
// 50cm. No imagery resolution resolves those — the ambiguity is in the
// registry, not in the picture — so the panel says so rather than letting the
// analyst hunt for a difference that is not visible.
const COLOCATED_M = 2;

function isColocatedWithNeighbour(candidates, i) {
  const d = candidates[i]?.distance_m;
  if (d == null) return false;
  return candidates.some(
    (o, j) => j !== i && o.distance_m != null && Math.abs(o.distance_m - d) <= COLOCATED_M,
  );
}

function NoCandidates() {
  return (
    <div className="rounded border border-accent-info/30 bg-accent-info/5 p-3">
      <p className="mb-1 text-xs font-medium text-accent-info">
        No registry well within 100m
      </p>
      {/* THE FINDING, NOT A MISSING PANEL. 164 records are here, and they are
          the reason the product exists: a detection with no RRC record nearby
          is something no state database can tell the customer. It must not
          read as data that failed to load. */}
      <p className="text-[11px] leading-relaxed text-fg-secondary">
        The search found no RRC surface record within the 100m radius. This is
        the finding — an apparent wellhead with no registry counterpart — not
        missing data.
      </p>
    </div>
  );
}

export default function CandidatePanel({
  candidates,
  hoveredRank,
  selectedRank,
  onHover,
  onSelect,
  holderRank = null,
  onToggleHolder,
}) {
  if (!candidates?.length) return <NoCandidates />;

  return (
    <ul className="space-y-1" data-testid="candidate-list">
      {candidates.map((c, i) => {
        const active = c.rank === selectedRank || c.rank === hoveredRank;
        const isSelected = c.rank === selectedRank;
        const colocated = isColocatedWithNeighbour(candidates, i);
        const holder = holderWording(c);
        const holderShown = holder != null && c.rank === holderRank;
        return (
          <li key={c.candidate_id ?? c.rank} data-testid={`candidate-row-${c.rank}`}>
            <button
              type="button"
              data-testid={`candidate-${c.rank}`}
              aria-pressed={isSelected}
              onMouseEnter={() => onHover?.(c.rank)}
              onMouseLeave={() => onHover?.(null)}
              onFocus={() => onHover?.(c.rank)}
              onBlur={() => onHover?.(null)}
              onClick={() => onSelect?.(c.rank)}
              className={[
                "w-full rounded border px-2 py-1 text-left text-xs transition-colors",
                isSelected
                  ? "border-accent-action/70"
                  : active
                    ? "border-white/20 bg-white/[0.05]"
                    : "border-bg-border bg-white/[0.02] hover:bg-white/[0.04]",
              ].join(" ")}
            >
              {/* PRIMARY LINE — what the analyst scans. Twelve of these at the
                  old 2x2 labelled grid was ~1,350px of scroll in a 560px pane;
                  the grid is generous for a panel that is scanned rather than
                  read, so it now only appears on the selected card. */}
              <div className="flex min-w-0 items-baseline gap-1.5">
                <span
                  className={[
                    "shrink-0 rounded px-1 text-[10px] font-semibold tabular-nums",
                    c.rank === 1
                      ? "bg-accent-info/20 text-accent-info"
                      : "bg-white/5 text-fg-secondary",
                  ].join(" ")}
                >
                  #{c.rank}
                </span>
                <span className="min-w-0 flex-1 truncate text-fg-primary">
                  <LeaseName value={c.rrc_lease_name} source={c.rrc_lease_name_source} />
                  {c.rrc_well_no ? ` ${c.rrc_well_no}` : ""}
                </span>
                <span className="shrink-0 tabular-nums text-fg-secondary">
                  {fmtDistance(c.distance_m)}
                  {bearingLabel(c.bearing)}
                </span>
              </div>

              {/* SECONDARY LINE — present but quieter. MONOSPACE on the API:
                  10542436 / 10542536 / 10542534 / 10542560 differ only in
                  interior digits, and in a proportional face the digits do not
                  line up down the stack so the differences are invisible. A
                  fixed-width face makes them fall into columns. */}
              <div className="flex min-w-0 items-baseline gap-1.5 text-[11px] text-fg-secondary/70">
                <span className="shrink-0 font-mono tabular-nums">{c.rrc_api}</span>
                <span className="shrink-0">· {c.rrc_status_norm || "—"}</span>
                {c.rrc_oil_gas_code && <span className="shrink-0">· {c.rrc_oil_gas_code}</span>}
                {c.rrc_operator && (
                  <span className="min-w-0 truncate">· {c.rrc_operator}</span>
                )}
              </div>

              {/* Colocation stays on the card; contention sits just below it,
                  outside the button, because it carries a control of its own.
                  BOTH are shown where both apply — 53 records are contended AND
                  colocated, and they are different problems: contention says
                  another record holds this well, colocation says two registry
                  rows sit within 2m of each other. */}
              {colocated && (
                // Named on the card because it is the reject_reason the QGIS
                // analyst already has a word for — colocated_other_registry_record.
                <div className="mt-0.5 text-[10px] text-amber-300/80">
                  colocated with another registry record
                </div>
              )}

              {/* The selected card expands. In 3c this is the one being adopted,
                  so it is the one that earns full labelled detail. */}
              {isSelected && (
                <div
                  data-testid={`candidate-detail-${c.rank}`}
                  className="mt-1.5 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-1 border-t border-white/10 pt-1.5"
                >
                  <Field label="RRC API (8-char)">
                    <span className="font-mono tabular-nums">{c.rrc_api}</span>
                  </Field>
                  <Field label="Operator">{c.rrc_operator}</Field>
                  <Field label="Status">{c.rrc_status_norm}</Field>
                  <Field label="Oil / gas">
                    {c.rrc_oil_gas_code
                      ? `${c.rrc_oil_gas_code}${c.rrc_oil_gas_code === "O" ? " (oil)" : c.rrc_oil_gas_code === "G" ? " (gas)" : ""}`
                      : null}
                  </Field>
                </div>
              )}
            </button>

            {/* ── WHO HOLDS IT, AND A WAY TO SEE THEM ────────────────────────
                A SIBLING of the card, not inside it: the card is a <button>,
                and a button inside a button is invalid and unreachable by
                keyboard. Toggling this does not select the candidate — looking
                at the holder is not choosing an adopt target. */}
            {holder && (
              <div className="mt-0.5 flex min-w-0 items-start gap-1.5 px-2 text-[10px]">
                <span
                  data-testid={`candidate-taken-${c.rank}`}
                  data-holder-kind={holder.kind}
                  className={[
                    "min-w-0 flex-1",
                    // An analyst's verdict is the strong claim and keeps the
                    // alarm colour; an automatic assignment is a weaker one.
                    holder.kind === "matched" ? "text-accent-action" : "text-fg-secondary",
                  ].join(" ")}
                >
                  {holder.verb}{" "}
                  <span className="break-all font-mono">{holder.id}</span>
                  {holder.detail ? ` (${holder.detail})` : ""}
                  {/* Approving it here no longer fails: it opens the move
                      confirmation (takeFromHolder, 91a5361e). */}
                  {" — approving it here moves it"}
                </span>
                <button
                  type="button"
                  data-testid={`candidate-holder-toggle-${c.rank}`}
                  aria-pressed={holderShown}
                  aria-label={holderShown
                    ? "Hide the detection that holds this well"
                    : "Show the detection that holds this well on the map"}
                  title={holderShown ? "Hide on map" : "Show on map"}
                  onClick={() => onToggleHolder?.(c.rank)}
                  className={[
                    "flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 ring-1 transition-colors",
                    holderShown
                      ? "bg-white/[0.06] text-fg-primary ring-white/25"
                      : "text-fg-secondary ring-white/10 hover:bg-white/[0.04]",
                  ].join(" ")}
                >
                  <Crosshair size={11} />
                  {holderShown ? "hide" : "show"}
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
